/* Cart drawer behaviour + the add-to-cart state machine.

   Every mutation goes through the Cart AJAX API with `sections: 'opt-cart-drawer'`
   so Shopify returns freshly rendered Liquid for the drawer in the same round trip.
   That keeps money formatting, the package grouping, the per-day maths and the
   free-shipping logic in one place (snippets/opt-cart-body.liquid) instead of
   duplicated here.

   The package block has no per-jar stepper by design: a PERUSTA subscription is ten
   cart lines and letting someone decrement a single jar silently breaks the pack.
   Removing the package removes every one of its keys in a single /cart/update.js.

   OptCart.submit() owns the add-to-cart feel: pending -> success -> drawer. The PDP
   keeps ownership of the PAYLOAD (it builds the ten-line subscription body) and
   hands it here, so button states, error copy and drawer opening live in one file
   rather than being reimplemented by every buy button on the page. */
(function () {
  var SECTION = 'opt-cart-drawer';
  var MIN_BUSY = 260;   /* below this a spinner reads as a flicker, not feedback */
  var OK_HOLD = 620;    /* long enough to register the tick, short enough not to nag */
  var drawer, scrim, scroll, lastFocus = null;

  function el(id) { return document.getElementById(id); }

  /* The open transition needs one paint between `hidden = false` and adding .on,
     or the drawer jumps instead of sliding. requestAnimationFrame gives that, but
     rAF is FULLY PAUSED in a backgrounded tab, which left the drawer stranded:
     scrim unhidden, aria-hidden already false, but .on never applied and no way to
     recover on return. The timeout is the floor, so the drawer always opens. */
  function nextFrame(fn) {
    var done = false;
    var run = function () { if (done) return; done = true; fn(); };
    requestAnimationFrame(run);
    setTimeout(run, 40);
  }

  function refs() {
    drawer = el('OptCartDrawer');
    scrim = el('OptCartScrim');
    scroll = el('OptCartScroll');
    return !!drawer;
  }

  function focusables() {
    return drawer.querySelectorAll('a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])');
  }

  function open() {
    if (!refs()) return;
    lastFocus = document.activeElement;
    scrim.hidden = false;
    nextFrame(function () {
      scrim.classList.add('on');
      drawer.classList.add('on');
    });
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    var f = focusables();
    (f.length ? f[0] : drawer).focus();
  }

  function close() {
    if (!refs()) return;
    drawer.classList.remove('on');
    scrim.classList.remove('on');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    setTimeout(function () { if (scrim) scrim.hidden = true; }, 280);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function busy(on) { if (drawer) drawer.classList.toggle('is-busy', !!on); }

  /* Swap in the re-rendered section. The API returns the whole section wrapper,
     so pull out our scroll container and the header count from it. */
  function apply(html) {
    if (!html || !refs()) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var freshScroll = doc.getElementById('OptCartScroll');
    var freshTop = doc.querySelector('.opt-cd__top h2');
    if (freshScroll && scroll) scroll.innerHTML = freshScroll.innerHTML;
    var topH2 = drawer.querySelector('.opt-cd__top h2');
    if (freshTop && topH2) topH2.textContent = freshTop.textContent;
    syncCount();
  }

  function syncCount() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        document.querySelectorAll('[data-cart-count]').forEach(function (n) {
          n.textContent = c.item_count;
          n.style.display = c.item_count > 0 ? '' : 'none';
        });
      })
      .catch(function () {});
  }

  function post(url, body) {
    body.sections = SECTION;
    busy(true);
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (d && d.sections && d.sections[SECTION]) apply(d.sections[SECTION]);
        busy(false);
        return d;
      })
      .catch(function () { busy(false); flash('Päivitys ei onnistunut. Yritä uudelleen.'); });
  }

  /* ---------- add-to-cart state machine ---------- */

  function btnList(btns) {
    if (!btns) return [];
    if (btns.nodeType) return [btns];
    return Array.prototype.slice.call(btns);
  }

  function setState(btns, state, label) {
    btnList(btns).forEach(function (b) {
      if (!b.dataset.atcLabel) b.dataset.atcLabel = b.textContent.trim();
      b.classList.remove('is-atc-busy', 'is-atc-ok');
      if (state === 'busy') {
        b.classList.add('is-atc-busy');
        b.disabled = true;
        b.setAttribute('aria-busy', 'true');
      } else if (state === 'ok') {
        b.classList.add('is-atc-ok');
        b.disabled = true;
        b.removeAttribute('aria-busy');
        b.textContent = label || 'Lisätty ostoskoriin';
      } else {
        b.disabled = false;
        b.removeAttribute('aria-busy');
        b.textContent = b.dataset.atcLabel;
      }
    });
  }

  /* Errors are never an alert(): a modal dialog on top of a checkout flow is the
     fastest way to lose the sale. Short, human, dismissible, and the button always
     returns to a usable state. */
  function flash(msg) {
    var t = el('OptCartToast');
    if (!t) {
      t = document.createElement('div');
      t.id = 'OptCartToast';
      t.className = 'opt-cd__toast';
      t.setAttribute('role', 'alert');
      document.body.appendChild(t);
    }
    t.textContent = msg;
    t.classList.add('on');
    clearTimeout(t._timer);
    t._timer = setTimeout(function () { t.classList.remove('on'); }, 4200);
  }

  function reason(d, status) {
    if (status === 422 || (d && d.status === 422)) {
      return (d && d.description) || 'Tuotetta ei voi juuri nyt lisätä. Tarkista saatavuus.';
    }
    if (d && d.description) return d.description;
    return 'Lisääminen ei onnistunut. Yritä uudelleen.';
  }

  function submit(payload, btns) {
    var started = Date.now();
    setState(btns, 'busy');
    var code;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (r) { code = r.status; return r.json(); })
      .then(function (d) {
        if (!d || !d.items || code >= 400) throw new Error(reason(d, code));
        /* hold the spinner to a minimum so it reads as deliberate, not janky */
        var wait = Math.max(0, MIN_BUSY - (Date.now() - started));
        return new Promise(function (res) { setTimeout(res, wait); }).then(function () {
          setState(btns, 'ok');
          return window.OptCart.refresh();
        }).then(function () {
          return new Promise(function (res) { setTimeout(res, OK_HOLD); });
        }).then(function () {
          if (refs()) { open(); setState(btns, 'idle'); }
          else { window.location.href = '/cart'; }   /* never a dead end */
        });
      })
      .catch(function (e) {
        setState(btns, 'idle');
        flash(e && e.message ? e.message : 'Verkkovirhe. Yritä uudelleen.');
      });
  }

  /* Public: the PDP builds its own subscription payload and hands it to submit(). */
  window.OptCart = {
    open: open,
    close: close,
    submit: submit,
    flash: flash,
    refresh: function () {
      return fetch('/?sections=' + SECTION, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (d) { apply(d[SECTION]); })
        .catch(function () {});
    },
    openWithRefresh: function () {
      return window.OptCart.refresh().then(open);
    }
  };

  document.addEventListener('click', function (e) {
    var t = e.target;

    if (t.closest('[data-cart-open]')) { e.preventDefault(); window.OptCart.openWithRefresh(); return; }
    if (t.closest('[data-cart-close]') || t.id === 'OptCartScrim') { e.preventDefault(); close(); return; }

    var qty = t.closest('[data-cart-qty]');
    if (qty) {
      e.preventDefault();
      post('/cart/change.js', { id: qty.dataset.key, quantity: parseInt(qty.dataset.cartQty, 10) });
      return;
    }

    var rmPack = t.closest('[data-cart-remove-pack]');
    if (rmPack) {
      e.preventDefault();
      var holder = rmPack.closest('[data-pack-keys]');
      if (!holder) return;
      var updates = {};
      holder.dataset.packKeys.split(',').forEach(function (k) { if (k) updates[k] = 0; });
      post('/cart/update.js', { updates: updates });
    }
  });

  document.addEventListener('keydown', function (e) {
    if (!drawer || !drawer.classList.contains('on')) return;
    if (e.key === 'Escape') { close(); return; }
    if (e.key !== 'Tab') return;
    var f = focusables();
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refs);
  else refs();
})();
